import WaitingDots from 'components/WaitingDots';
import { Invoice } from 'models/Invoice';
import { InvoiceContent } from 'models/InvoiceContent';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Alert, Card, CardBody } from 'reactstrap';
import { bindActionCreators } from 'redux';
import { RootState } from 'store/store';
import { addInvoiceContent } from "store/actions/Billing";
import * as api from 'services/Api';
import CreatableSelect from 'react-select/creatable';
import { useOfficeTranslation } from 'i18n/OfficeTranslation';

interface Props {
    invoice: Invoice;
}

interface Actions {
    addInvoiceContent: (ic: InvoiceContent, invoiceId: string) => void;
}

interface TypeOption {
    value: string;
    label: string;
    suggestion?: api.InvoiceContentSuggestion;
}

const InvoiceContentAddCard: React.FC<Props & Actions> = ({ addInvoiceContent, invoice }) => {
    const { t } = useOfficeTranslation();

    const [content, setContent] = useState<InvoiceContent>(new InvoiceContent())
    const [suggestions, setSuggestions] = useState<api.InvoiceContentSuggestion[]>([])
    const [typeInput, setTypeInput] = useState<string>("")
    const [error, setError] = useState<string>("")
    const [invoiceState] = useState<Invoice>(invoice)

    useEffect(() => {
        if (!invoice?.customer) {
            setSuggestions([])
            return
        }

        const timeout = setTimeout(() => {
            api.getInvoiceContentSuggestions(invoice.customer, typeInput, invoice.id)
                .then(setSuggestions)
                .catch(() => setSuggestions([]))
        }, 250)

        return () => clearTimeout(timeout)
    }, [invoice?.customer, invoice?.id, typeInput])

    const typeOptions = useMemo<TypeOption[]>(() => suggestions.map(suggestion => ({
        value: suggestion.type,
        label: suggestion.type,
        suggestion,
    })), [suggestions])

    const selectedTypeOption = content.type
        ? typeOptions.find(option => option.value.toLowerCase() === content.type.toLowerCase()) || { value: content.type, label: content.type }
        : null

    const updateContent = (patch: Partial<InvoiceContent>) => {
        setContent(prev => ({ ...prev, ...patch } as InvoiceContent))
    }

    const updateType = (type: string) => {
        const suggestion = suggestions.find(s => s.type.toLowerCase() === type.trim().toLowerCase())
        if (suggestion) {
            updateContent({
                type,
                uom: suggestion.uom,
                price: suggestion.price,
                tax_rate: suggestion.tax_rate,
            })
            return
        }
        updateContent({ type })
    }

    const commitTypeOption = (option: TypeOption | null) => {
        if (!option) {
            updateContent({ type: "" })
            setTypeInput("")
            return
        }

        if (option.suggestion) {
            updateContent({
                type: option.suggestion.type,
                uom: option.suggestion.uom,
                price: option.suggestion.price,
                tax_rate: option.suggestion.tax_rate,
            })
            setTypeInput(option.suggestion.type)
            return
        }

        updateType(option.value)
        setTypeInput(option.value)
    }

    const numberValue = (value?: number) => Number.isFinite(value) ? value : ""

    const addContent = () => {

        if (
            invoiceState
            && (content.type !== "" && content.type !== undefined)
            && (content.name !== "" && content.name !== undefined)
            && Number.isFinite(content.amount)
            && Number.isFinite(content.price)
            && Number.isFinite(content.tax_rate)
            && content.tax_rate >= 0
            && content.tax_rate <= 100
        ) {
            setError("")
            addInvoiceContent(content, invoiceState.id)
                //@ts-ignore
                .then(() => {
                    setContent(new InvoiceContent())
                })
        } else {
            setError(t("invoice.invoiceContentAddCard.typeNameAmountPriceAndAVatRateBetween0PercentageAnd100PercentageAreMandatory"))
        }

    }


    if (invoice === undefined) {
        return <WaitingDots />
    }

    if (invoice.draft || invoice.quote) {
        return <Card>
            <CardBody>
                {
                    error !== "" &&
                    <Alert color="danger">{error}</Alert>
                }
                <h4 className="invoice-section-title" id="invoice-add-line-title">{t("invoice.invoiceContentAddCard.addContent")}</h4>
                <p className="text-muted invoice-add-line-description">{t("invoice.invoiceContentAddCard.instructions")}</p>
                <form className="invoice-add-line-form" aria-labelledby="invoice-add-line-title"
                    onSubmit={event => { event.preventDefault(); addContent(); }}>
                    <div className="invoice-add-line-identity">
                        <div className="invoice-add-line-field">
                            <label htmlFor="invoice-line-type">{t("invoice.invoiceContentAddCard.service")}</label>
                            <CreatableSelect
                                inputId="invoice-line-type"
                                classNamePrefix="ledger-select"
                                value={selectedTypeOption}
                                options={typeOptions}
                                isClearable={true}
                                isSearchable={true}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                placeholder={t("invoice.invoiceContentAddCard.searchOrEnterAType")}
                                noOptionsMessage={() => t("invoice.invoiceContentAddCard.noTypeFound")}
                                formatCreateLabel={(inputValue: string) => t("invoice.invoiceContentAddCard.useValue", {value: inputValue})}
                                formatOptionLabel={(option: TypeOption) => (
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span>{option.label}</span>
                                        {option.suggestion &&
                                            <small className="text-muted">
                                                € {option.suggestion.price.toFixed(2)} / {option.suggestion.uom} · {option.suggestion.tax_rate.toFixed(2)}{t("invoice.invoiceContentAddCard.vatRateSuffix")}
                                            </small>
                                        }
                                    </div>
                                )}
                                onInputChange={(inputValue: string, meta: { action: string }) => {
                                    if (meta.action === "input-change") {
                                        setTypeInput(inputValue)
                                    }
                                }}
                                onChange={(option: TypeOption | null) => commitTypeOption(option)}
                                styles={{
                                    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                                    control: (base: any) => ({
                                        ...base,
                                        minHeight: 44,
                                        borderColor: "#ced4da",
                                        boxShadow: "none",
                                        "&:hover": {
                                            borderColor: "#adb5bd",
                                        },
                                    }),
                                }}
                            />
                        </div>
                        <div className="invoice-add-line-field">
                            <label htmlFor="invoice-line-name">{t("common.name")}</label>
                            <input id="invoice-line-name" name="name" type="text" className="form-control"
                                value={content.name || ""} onChange={e => updateContent({ name: e.currentTarget.value })} />
                        </div>
                        <div className="invoice-add-line-field">
                            <label htmlFor="invoice-line-description">{t("common.description")}</label>
                            <input id="invoice-line-description" name="description" type="text" className="form-control"
                                value={content.description || ""} onChange={e => updateContent({ description: e.currentTarget.value })} />
                        </div>
                    </div>
                    <div className="invoice-add-line-values">
                        <div className="invoice-add-line-field">
                            <label htmlFor="invoice-line-qty">{t("common.qty")}</label>
                            <input id="invoice-line-qty" name="qty" type="number" step="any" className="form-control"
                                value={numberValue(content.amount)} onChange={e => updateContent({ amount: parseFloat(e.currentTarget.value) })} />
                        </div>
                        <div className="invoice-add-line-field">
                            <label htmlFor="invoice-line-uom">{t("common.uom")}</label>
                            <input id="invoice-line-uom" name="uom" type="text" className="form-control"
                                value={content.uom || ""} onChange={e => updateContent({ uom: e.currentTarget.value })} />
                        </div>
                        <div className="invoice-add-line-field">
                            <label htmlFor="invoice-line-price">{t("common.price")}</label>
                            <input id="invoice-line-price" name="price" type="number" step="any" className="form-control"
                                value={numberValue(content.price)} onChange={e => updateContent({ price: parseFloat(e.currentTarget.value) })} />
                        </div>
                        <div className="invoice-add-line-field">
                            <label htmlFor="invoice-line-tax-rate">{t("invoice.invoiceContentAddCard.vatPercentage")}</label>
                            <input id="invoice-line-tax-rate" name="tax-rate" type="number" min={0} max={100} step="0.01" className="form-control"
                                value={numberValue(content.tax_rate)} onChange={e => updateContent({ tax_rate: parseFloat(e.currentTarget.value) })} />
                        </div>
                        <button type="submit" className="btn btn-primary invoice-add-line-submit">
                            <i className="mdi mdi-plus" aria-hidden="true" /> {t("invoice.invoiceContentAddCard.addContent")}
                        </button>
                    </div>
                </form>
            </CardBody>
        </Card>
    }

    return <></>
}

const mapStateToProps = (state: RootState) => {
    return {
        invoices: state.Invoices,
    };
};


const mapDispatchToProps = (dispatch: any) => {
    return bindActionCreators({ addInvoiceContent }, dispatch);
};

export default connect(mapStateToProps, mapDispatchToProps)(InvoiceContentAddCard);
